import React from "react";

export const stringLiterals = {
  appTitle: "Course Assistant Application Management System",
  userProfileTitle: "User Profile",
  applicationTitle: "Semester Application",
  courseSelectionTitle: "Selected Courses",
};

export const enums = {
  term: ["Intersession", "Spring", "Summer", "Fall"],
  role: ["STUDENT", "FACULTY", "ADMIN"],
  classYear: [
    { value: "Sophomore", label: "Sophomore" },
    { value: "Junior", label: "Junior" },
    { value: "Senior", label: "Senior" },
    {
      value: "Combined Bachelors/Masters",
      label: "(combined student - deprecated, DO NOT SELECT)",
    },
    { value: "Masters (first semester)", label: "Masters (first semester)" },
    {
      value: "Masters (second semester and beyond)",
      label: "Masters (second semester and beyond)",
    },
    { value: "Doctorate (PhD)", label: "Doctorate (PhD)" },
  ],
  teachingExperience: [
    "None",
    "Some but not in JHU CS or Gateway courses",
    "1 semester as CA",
    "2 or more semesters as CA",
    "Head/Lead",
    "1 or more semesters as TA",
  ],
  enrollmentStatus: [
    "Full-time US citizen",
    "Full-time International",
    "Part-time International",
  ],
  residency: ["In the US", "Not in the US", "Don't know yet"],
  payroll: [
    "This or last semester",
    "Two semesters ago",
    "Three or more semesters ago",
    "Never been",
  ],
  grades: [
    "Currently taking",
    "Took elsewhere",
    "A+",
    "A",
    "A-",
    "B+",
    "B",
    "Pass/Satisfactory", // FIXME: this used to be "Pass/Satisfactory, Transfer credit" in the past so the data may not match correctly!
    "Other",
  ],
  courseExperience: ["None in this course", "Returning CA", "Head/Lead/TA"],
  yesNo: ["Yes", "No"],
  instructorFeedback: [
    "Needs reviewing",
    "Check on S* grade",
    "Do not hire for this course",
    "Needs technical interview",
    "Interview scheduled",
    "Borderline for course",
    "Above bar",
    "Need schedule info",
    "Request hire as regular CA",
    "Priority hire as regular CA",
    "Head/Lead consideration",
    "Request head/lead hire",
  ],
  applicationStatus: ["Offer made", "Submitted", "Not selected", "Withdrawn"],
  hiringSeasonStatus: ["Open", "Closed"],
  adminFeedback: [
    "Needs vetting",
    "Application vetted",
    "Application withdrawn",
    "Do not hire for any course",
    "Interview (low priority)",
    "Interview (general)",
    "Interview (invited)",
    "Prior review mediocre/poor",
    "Weak interview",
    "Worth consideration",
    "Strong consideration",
    "New hire for course tbd",
    "Rehire for course tbd",
    "Hire for specific course",
    "Head/lead consideration",
    "Head/lead hire",
  ],
  evaluation: [
    "Not applicable",
    "No prior experience",
    "Not evaluated",
    "Poor job - do not rehire",
    "Mediocre job - needs improvement",
    "Fine job",
    "Superb job",
  ],
};

export const userProfileFormSchema = [
  {
    label: "Legal First Name",
    name: "firstName",
    type: "text",
    feedback: "Cannot be empty!",
    help: "Your first and last names must be exactly as it appears on SIS (<a href='https://sis.jhu.edu/sswf/' target='_blank'>Student Information System</a>).",
    required: true,
  },
  {
    label: "Last Name",
    name: "lastName",
    type: "text",
    feedback: "Cannot be empty!",
    help: 'See the note for "First Name."',
    required: true,
  },
  {
    label: "Preferred Name",
    name: "preferredName",
    type: "text",
    required: true,
    feedback: "Cannot be empty!",
    help: "This is the name you would like to be called by.",
  },
  {
    label: "Phone",
    name: "phone",
    type: "text",
    help: "Enter 10 digits only.",
    feedback:
      "Invalid format: Must be in ########## (10 digits). Alternatively, you can leave this blank.",
  },
  {
    label: "Email",
    name: "email",
    type: "text",
    feedback: "Must be a correctly formatted email address.",
    help: "Preferably your JHU Email.",
    required: true,
  },
];

export const userProfileFormValidation = (key, value) => {
  switch (key) {
    case "phone":
      return /^(\d{10})$/.test(value) || !value || value.length === 0;
    case "email":
      return /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/.test(
        value
      );
    default:
      return value && value.length !== 0;
  }
};

const linkFormatter = (value) => {
  return (
    <div>
      Click{" "}
      <a href={value} target="_blank" rel="noopener noreferrer">
        here
      </a>{" "}
      to open the link
    </div>
  );
};

export const studentProfileFormSchema = [
  {
    label: "Program of Study",
    name: "program",
    type: "text",
    feedback: "Cannot be empty!",
    help: "List all majors/minors.",
    required: true,
  },
  {
    label: "Cumulative GPA",
    name: "gradePointAverage",
    type: "text",
    feedback: "Value: must be at least 3.0 to apply",
    help: "Cumulative on 4.0 scale.",
    required: true,
  },
  {
    label: "Class Year",
    name: "classYear",
    as: "select",
    type: "text",
    options: ["", ...enums.classYear],
    feedback: "Cannot be empty!",
    help: "In semester for which you are applying; may not be a freshman.",
    required: true,
  },
  {
    label: "Teaching Experience",
    name: "experience",
    as: "select",
    type: "text",
    options: ["", ...enums.teachingExperience],
    feedback: "Cannot be empty!",
    help: "Your highest-level teaching experience in CS courses or Gateway at JHU",
    required: true,
  },
  {
    label: "Other Experience",
    name: `comment`,
    as: "textarea",
    type: "text",
    help: "List/highlight any other academic/teaching/work experience that may distinguish you from other applicants.",
  },
  {
    label: "Credits",
    name: "creditHours",
    type: "text",
    feedback: "Must be a number greater than 0 and less than 20.",
    help: "What is your intended course/credit load in the semester for which you are applying?",
    required: true,
  },
  {
    label: "Available Hours",
    name: "availableHours",
    type: "text",
    feedback: "Must be a number greater than 0 and less than 20.",
    help: "How many hours per week can you work? CAs usually work 8-12, Heads work 15-19. There is a strict limit of less than 20 hours per week for all campus jobs combined.",
    required: true,
  },
  {
    label: "Other Job Hours",
    name: "otherJobHours",
    type: "text",
    feedback: "Must be a number greater than or equal to 0 but less than 20.",
    help: "How many hours per week do you expect to work in other jobs? There is a student employment limit of less than 20 hours total for all campus jobs combined.",
    required: true,
  },
  {
    label: "Enrollment Status",
    name: "enrollmentStatus",
    as: "select",
    type: "text",
    options: ["", ...enums.enrollmentStatus],
    feedback: "Cannot be empty!",
    help: "Citizens must be full-time students for student employment eligibility.",
    required: true,
  },
  {
    label: "Residency",
    name: "residency",
    as: "select",
    type: "text",
    options: ["", ...enums.residency],
    feedback: "Cannot be empty!",
    help: "Where will you be living during the semester for which you are applying?",
    required: true,
  },
  {
    label: "Work-Study",
    name: "workStudy",
    as: "select",
    type: "text",
    options: ["", ...enums.yesNo],
    feedback: "Cannot be empty!",
    help: "Are you eligible for Federal Work-Study funds? (undergrads only; must be US citizen)<br/>Refer to JHU's <a href='https://studentaffairs.jhu.edu/studentemployment/employer-information/employer-handbook/fws-guidelines/' target='_blank'>Employer Handbook</a> to learn more about Work-Study Permits.",
    required: true,
  },
  {
    label: "Payroll",
    name: "payroll",
    as: "select",
    type: "text",
    options: ["", ...enums.payroll],
    feedback: "Cannot be empty!",
    help: "What is the most recent semester that you were on JHU payroll (for any Homewood department)?",
    required: true,
  },
  {
    label: "Transcript",
    name: "transcript",
    type: "text",
    help: 'Please download an unofficial transcript from SIS, upload it to cloud storage and provide a link here. It is recommended to upload the file to JHU\'s OneDrive and share it with "People in Johns Hopkins with the link."<br/><b>If you are a graduate student, please share a folder that contains your transcripts from JHU and prior schools.</b>',
    view: (value) => linkFormatter(value),
    required: true,
    feedback: "Cannot be empty!",
  },
  {
    label: "Resume",
    name: "resume",
    type: "text",
    help: 'It is <b>optional</b> but <u>highly recommended</u> to provide your resume. Please see upload note for "Transcript."',
    view: (value) => linkFormatter(value),
  },
  {
    label: "LinkedIn",
    name: "linkedin",
    type: "text",
    help: "You may choose to share a link to your profile on LinkedIn or other (professional social) platforms (or your personal website) where we can see your academic or work portfolio. (Please provide only one link.)",
    view: (value) => linkFormatter(value),
  },
  {
    label: "Referee Name",
    name: "refereeName",
    type: "text",
    help: "You must provide the name and email address of a <i>referee</i>, a person who is willing (if asked) to give a <i>reference letter</i> supporting your application. It is recommended to choose a CS Faculty as a referee.",
    feedback: "Cannot be empty!",
    required: true,
  },
  {
    label: "Referee Email",
    name: "refereeEmail",
    type: "text",
    help: 'See the note about "Referee Name."',
    feedback: "Must be a correctly formatted email address.",
    required: true,
  },
];

export const courseSelectionFormSchema = (choice, courses) => {
  return [
    {
      label: "Course",
      name: `course${choice}`,
      type: "text",
      as: "select",
      options: courses,
      help: "You should have taken the course and gotten above a B grade or have equivalent experience.",
      feedback:
        "The first choice cannot be empty. You may leave other choices empty. The choices must be different from one another!",
      required: choice === 1,
    },
    {
      label: "Grade",
      name: `grade${choice}`,
      as: "select",
      type: "text",
      options: ["", ...enums.grades],
      feedback: "Cannot be empty!",
      help: "Only select a letter grade if it is on your JHU transcript; otherwise use one of the other options.",
      required: choice === 1,
    },
    {
      label: "Experience",
      name: `experience${choice}`,
      as: "select",
      type: "text",
      options: ["", ...enums.courseExperience],
      feedback: "Cannot be empty!",
      help: "Teaching experience specific to this course.",
      required: choice === 1,
    },
    {
      label: "Head/Lead",
      name: `head${choice}`,
      as: "select",
      type: "text",
      options: ["", ...enums.yesNo],
      feedback: "Cannot be empty!",
      help: "Are you interested in head/lead position if available?",
    },
    {
      label: "Comment",
      name: `comment${choice}`,
      as: "textarea",
      type: "text",
      help: "You can use this section to (briefly) highlight any academic/teaching/work experience relevant to this course.",
    },
  ];
};

export const applicationFormValidation = (key, value, data) => {
  // FIXME: for some reason, the value used to always be a string
  // but now it can be other types. It is probably because of dependency
  // updates. As a quick fix, I am converting the value to a string.
  value = String(value);
  // console.log({ value, type: typeof value });
  switch (key) {
    case "resume":
    case "linkedin":
    case "comment":
    case "comment1":
    case "comment2":
    case "comment3":
    case "comment4":
    case "head1":
    case "head2":
    case "head3":
    case "head4":
      return true;
    case "creditHours":
    case "availableHours":
      return (
        /^(\d{1,2})$/.test(value) &&
        value.trim().length !== 0 &&
        Number(value) > 0 &&
        Number(value) < 20
      );
    case "otherJobHours":
      // difference with availableHours and creditHours: otherHours can be zero
      return (
        /^(\d{1,2})$/.test(value) &&
        value.trim().length !== 0 &&
        Number(value) >= 0 &&
        Number(value) < 20
      );
    case "gradePointAverage":
      return Number(value) <= 4.0 && Number(value) >= 2.8;
    case "refereeEmail":
      return /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/.test(
        value
      );
    case "course1":
      return (
        value.trim().length !== 0 &&
        value !== data["course2"] &&
        value !== data["course3"] &&
        value !== data["course4"]
      );
    case "course2":
      return (
        value.trim().length === 0 ||
        (value !== data["course1"] &&
          value !== data["course3"] &&
          value !== data["course4"])
      );
    case "course3":
      return (
        value.trim().length === 0 ||
        (value !== data["course1"] &&
          value !== data["course2"] &&
          value !== data["course4"])
      );
    case "course4":
      return (
        value.trim().length === 0 ||
        (value !== data["course1"] &&
          value !== data["course2"] &&
          value !== data["course3"])
      );
    case "grade2":
    case "experience2":
      return data["course2"].length === 0 || value.trim().length !== 0;
    case "grade3":
    case "experience3":
      return data["course3"].length === 0 || value.trim().length !== 0;
    case "grade4":
    case "experience4":
      return data["course4"].length === 0 || value.trim().length !== 0;
    default:
      return value.trim().length !== 0;
  }
};
